import React from 'react';
import { FileEdit } from 'lucide-react';
import type { ParagraphIndexEntry, StructuredDocumentEdit } from './portalParagraphMap';

interface PortalParagraphHitlPreviewProps {
  edit: StructuredDocumentEdit;
  paragraph?: ParagraphIndexEntry | null;
  onAccept?: () => void;
  onDismiss?: () => void;
  disabled?: boolean;
}

/**
 * HITL-Vorschau für strukturierte Absatz-Edits (Phase 1.4).
 * Kein OOXML-Write — nur Vorschau + Bestätigung für Phase 2.
 */
export const PortalParagraphHitlPreview: React.FC<PortalParagraphHitlPreviewProps> = ({
  edit,
  paragraph,
  onAccept,
  onDismiss,
  disabled = false,
}) => {
  const original = paragraph?.snippet || edit.originalSnippet || '—';

  return (
    <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.06] p-3 space-y-2 max-w-[min(100%,28rem)]">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200/90">
        <FileEdit className="w-3.5 h-3.5" aria-hidden />
        Absatz {edit.index} — Vorschlag (HITL)
      </div>
      <div className="text-[10px] text-slate-500">Aktuell</div>
      <p className="text-[11px] text-slate-400 leading-snug whitespace-pre-wrap border-l-2 border-white/10 pl-2">
        {original}
      </p>
      <div className="text-[10px] text-slate-500">Vorschlag</div>
      <p className="text-[11px] text-slate-200 leading-snug whitespace-pre-wrap border-l-2 border-amber-400/40 pl-2">
        {edit.proposedText}
      </p>
      {edit.rationale ? (
        <p className="text-[10px] text-slate-500 italic">{edit.rationale}</p>
      ) : null}
      <p className="text-[9px] text-slate-600">
        Phase 1.4: Nur Vorschau — Übernahme in den Editor folgt in Phase 2 (strukturiertes OOXML).
      </p>
      {(onAccept || onDismiss) && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {onAccept ? (
            <button
              type="button"
              disabled={disabled}
              onClick={onAccept}
              className="rounded-md border border-amber-400/30 bg-amber-500/15 px-2 py-1 text-[10px] font-medium text-amber-100 hover:bg-amber-500/25 disabled:opacity-50"
            >
              Vorschlag merken
            </button>
          ) : null}
          {onDismiss ? (
            <button
              type="button"
              disabled={disabled}
              onClick={onDismiss}
              className="rounded-md border border-white/10 px-2 py-1 text-[10px] text-slate-400 hover:text-slate-200 disabled:opacity-50"
            >
              Schließen
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
};

interface PortalParagraphContextChipProps {
  entry: ParagraphIndexEntry;
}

/** Kompakte Anzeige wenn User „Absatz N" referenziert. */
export const PortalParagraphContextChip: React.FC<PortalParagraphContextChipProps> = ({ entry }) => (
  <span
    className="inline-flex items-center gap-1 rounded-full border border-violet-400/25 bg-violet-500/10 px-2 py-0.5 text-[10px] text-violet-100"
    title={entry.snippet}
  >
    Absatz {entry.index}
  </span>
);
